<template>
  <section id="about" class="py-16 sm:py-24 bg-white dark:bg-gray-900 relative overflow-hidden transition-colors duration-500">
    <div class="max-w-5xl mx-auto px-6 relative z-10">
      <!-- Section header -->
      <div class="mb-12 sm:mb-16">
        <span class="inline-block px-3 py-1 bg-amber-100/80 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-semibold text-xs rounded-full mb-3">{{ t('aboutMe') }}</span>
        <h2 class="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight">
          {{ t('aboutHeading') }}
        </h2>
      </div>

      <div class="grid lg:grid-cols-5 gap-12 lg:gap-16">
        <!-- Left: About text (3 cols) -->
        <div class="lg:col-span-3 space-y-5">
          <p class="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            {{ t('aboutText1', { highlight: t('fullStackDeveloper'), years: yearsOfExperience }) }}
          </p>
          <p class="text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            {{ t('aboutText2') }}
          </p>

          <!-- Quick stats -->
          <div class="flex items-center gap-8 pt-6 border-t border-gray-100 dark:border-gray-800">
            <div>
              <div class="text-2xl sm:text-3xl font-extrabold font-mono gradient-text">{{ yearsOfExperience }}+</div>
              <div class="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5 font-medium">{{ t('yearsExperience') }}</div>
            </div>
            <div class="w-px h-10 bg-gray-200 dark:bg-gray-700"></div>
            <div>
              <div class="text-sm font-semibold text-gray-900 dark:text-white">Pontus Vision</div>
              <div class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Oct 2022 — Present</div>
            </div>
          </div>
        </div>

        <!-- Right: Skills grid (2 cols) -->
        <div class="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
          <div v-for="skill in skills" :key="skill.key"
            class="group flex items-start gap-3.5 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-800 hover:border-amber-200 dark:hover:border-amber-800 transition-colors"
          >
            <div class="w-9 h-9 shrink-0 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center mt-0.5">
              <component :is="skill.icon" class="w-4.5 h-4.5 text-amber-700 dark:text-amber-400" />
            </div>
            <div>
              <h3 class="font-semibold text-gray-900 dark:text-white text-sm">{{ t(skill.key) }}</h3>
              <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mt-0.5">{{ t(skill.descKey) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, h } from 'vue'
import { useI18n } from '@/i18n'

const { t } = useI18n()

const yearsOfExperience = computed(() => {
  const startDate = new Date(2022, 9, 1)
  const now = new Date()
  const years = (now - startDate) / (1000 * 60 * 60 * 24 * 365.25)
  return Math.floor(years)
})

const IconMonitor = { render: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24', class: 'w-[18px] h-[18px]' }, [h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' })]) }
const IconServer = { render: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24', class: 'w-[18px] h-[18px]' }, [h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2' })]) }
const IconBulb = { render: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24', class: 'w-[18px] h-[18px]' }, [h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' })]) }
const IconCloud = { render: () => h('svg', { fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24', class: 'w-[18px] h-[18px]' }, [h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12' })]) }

const skills = [
  { key: 'frontend', descKey: 'frontendDesc', icon: IconMonitor },
  { key: 'backend', descKey: 'backendDesc', icon: IconServer },
  { key: 'aiMl', descKey: 'aiMlDesc', icon: IconBulb },
  { key: 'devops', descKey: 'devopsDesc', icon: IconCloud },
]
</script>
